<template>
  <div class="w-full grid grid-cols-5 gap-10">
    <v-dialog v-model="addDocumentDialog" persistent width="500">
      <template v-slot:activator="{ props }">
        <button v-bind="props" class="bg-primary-main/80 !text-white rounded-lg shadow-lg">
          <div class="p-5 flex flex-col items-center">
            <v-icon icon="mdi-plus-circle-outline" :size="40"></v-icon>
            <span class="text-center"
              >Create <br />
              New Document</span
            >
          </div>

          <hr class="border-white" />

          <div class="p-3 flex items-center justify-center">
            <Icon :icon="'ant-design:edit-outlined'" class="text-xl" />
          </div>
        </button>
      </template>
      <v-card>
        <v-card-title>
          <span class="text-h5">New Document</span>
        </v-card-title>
        <v-card-text>
          <v-container>
            <v-row>
              <v-col cols="12">
                <TlTextField labelText="Document Name" v-model="formData.title" size="small" />
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            variant="flat"
            class="!py-3 !bg-primary-light !rounded-xl !tracking-normal !capitalize !h-auto min-w-[120px]"
            @click="addDocumentDialog = false"
          >
            Close
          </v-btn>
          <v-btn
            variant="flat"
            class="!py-3 !bg-primary-main !rounded-xl !tracking-normal !capitalize !text-white !h-auto min-w-[120px]"
            @click="newDocument"
            :disabled="!formData.title"
            :loading="loadingResponse"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="uploadDocumentDialog" persistent width="500">
      <template v-slot:activator="{ props }">
        <button v-bind="props" class="bg-primary-main/80 !text-white rounded-lg shadow-lg">
          <div class="p-5 flex flex-col items-center">
            <v-icon icon="mdi-plus-circle-outline" :size="40"></v-icon>
            <span class="text-center"
              >Upload <br />
              Existing Document</span
            >
          </div>

          <hr class="border-white" />

          <div class="p-3 flex items-center justify-center">
            <Icon :icon="'ant-design:upload-outlined'" class="text-xl" />
          </div>
        </button>
      </template>
      <v-card>
        <v-card-title>
          <span class="text-h5">Upload Document</span>
        </v-card-title>
        <v-card-text>
          <v-container>
            <v-row>
              <v-col cols="12">
                <TlTextField labelText="Document Name" v-model="formData.title" size="small" />
                <div class="grid grid-cols-1 space-y-2">
                  <label class="pt-8">Attach Document</label>
                  <div class="flex items-center justify-center w-full">
                    <label
                      class="cursor-pointer flex flex-col rounded-lg border-4 border-dashed w-full h-60 p-10 group text-center"
                    >
                      <div
                        class="h-full w-full text-center flex flex-col items-center justify-center items-center"
                      >
                        <div class="flex flex-auto mx-auto">
                          <Icon
                            color="grey"
                            :icon="'ant-design:cloud-upload-outlined'"
                            height="100"
                          />
                        </div>
                        <p class="pointer-none text-gray-500">
                          Drag and drop files here <br />
                          or
                          <span class="text-blue-600 hover:underline">select a file</span>
                          from your computer
                        </p>
                        <p id="fileName" class="pointer-none text-gray-500"></p>
                      </div>
                      <input
                        accept=".doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                        type="file"
                        v-on:change="processDocument($event)"
                        class="hidden"
                      />
                    </label>
                  </div>
                </div>
                <p class="text-sm text-gray-300">
                  <span>File type: doc,docx</span>
                </p>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            variant="flat"
            class="!py-3 !bg-primary-light !rounded-xl !tracking-normal !capitalize !h-auto min-w-[120px]"
            @click="uploadDocumentDialog = false"
          >
            Close
          </v-btn>
          <v-btn
            @click="newDocument"
            variant="flat"
            class="!py-3 !bg-primary-main !rounded-xl !tracking-normal !capitalize !text-white !h-auto min-w-[120px]"
            :disabled="!formData.title || !formData.data"
            :loading="loadingResponse"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <button
      v-for="(document, index) in documents"
      :key="index"
      class="bg-[#fafafa] !text-black text-opacity-90 rounded-lg shadow-lg"
    >
      <div @click="openDocument(document.id)" class="p-3 flex flex-col items-center">
        <span class="text-center">
          {{ document.title }}
        </span>
      </div>

      <hr class="border-grey" />

      <div @click="openDocument(document.id)" class="p-5 flex flex-col items-center">
        <span
          @click="openDocument(document.id)"
          class="text-center text-xs text-grey text-opacity-10"
          >Last edit {{ document.date }}</span
        >
      </div>

      <hr class="border-grey" />

      <div class="p-3 flex items-center justify-center w-full">
        <div
          @click="downloadDocument(document.data, document.title)"
          class="w-1/2 flex flex-col items-center justify-center"
        >
          <Icon :icon="'ant-design:download-outlined'" class="text-xl" />
        </div>
        <span class="text-grey text-opacity-10">|</span>
        <TlPopConfirm
          title="Are you sure you want to delete this document?"
          :subTitle="'NOTE: This document can be restored from the \'Trash\' section within 30 days.'"
          okText="Yes, Delete"
          cancelText="No"
          @confirm="trashDocument(document.id)"
        >
          <div class="w-1/2 flex flex-col items-center justify-center">
            <Icon :icon="'ant-design:delete-outlined'" class="text-xl" />
          </div>
        </TlPopConfirm>
      </div>
    </button>

    <div v-if="!documents.length" class="grid place-items-center content-center">
      <figure class="mx-auto max-w-[100px] w-[150px] aspect-square">
        <img src="@/assets/icons/Empty.svg" alt="Empty" class="w-full h-full object-contain" />
      </figure>

      <span class="text-grey">Get started</span>
    </div>
  </div>
</template>

<script lang="ts">
import { Vue, Options } from 'vue-class-component';
import { Icon } from '@iconify/vue';
import TlPopConfirm from '@/components/global/TlPopConfirm.vue';
import { Watch } from 'vue-property-decorator';
import {
  createDocument,
  fetchDocuments,
  extractDocument,
  downloadDocument,
  trashDocument
} from '../../actions/document';
import TlTextField from '@/components/global/TlTextField.vue';
import { usePayloadStore } from '@/stores/payload';
import Message from 'antd-message';
import $ from 'jquery';
import { saveAs } from 'file-saver';
import 'ant-design-vue/lib/popconfirm/style/css';

@Options({
  components: {
    TlTextField,
    TlPopConfirm,
    Icon
  }
})
export default class Documents extends Vue {
  mounted() {
    this.getDocuments();
  }
  private payloadStore = usePayloadStore();

  public loadingResponse = false;

  public addDocumentDialog = false;

  public uploadDocumentDialog = false;

  public formData = {
    title: '',
    data: ''
  };

  @Watch('payloadStore.payload.newDocument')
  public onNewDocumentClick() {
    this.checkNewDocumentDialog();
  }

  public checkNewDocumentDialog() {
    if (this.payloadStore.payload.newDocument) {
      this.addDocumentDialog = true;
    }
    this.payloadStore.change({ newDocument: false });
  }

  public openDocument(id: any) {
    this.payloadStore.change({ displayedFeatured: false, selectedModule: '', document: { id } });
    window.location.href = '/functions';
  }

  public processDocument(e: any) {
    $('#fileName').html(e.target.files[0].name);
    const file = e.target.files[0];
    const formData = new FormData();
    formData.append('file', file);
    this.payloadStore.change({ loadingResponse: true });
    extractDocument(formData)
      .then((res: any) => {
        this.formData.data = res.data;
        this.payloadStore.change({ loadingResponse: false });
        Message.success('Document processed and ready to be uploaded');
      })
      .catch((err) => {
        this.payloadStore.change({ loadingResponse: false });
        Message.error('Document could not be extracted');
      });
  }

  public newDocument() {
    this.loadingResponse = true;
    createDocument({ data: this.formData.data, title: this.formData.title })
      .then((res: any) => {
        this.loadingResponse = false;
        this.addDocumentDialog = false;
        this.uploadDocumentDialog = false;
        if (this.payloadStore.payload.documentsCount == 0) {
          this.payloadStore.change({ firstDocument: true });
          this.openDocument(res.id)
        }
        this.getDocuments();
        Message.success('Document created successfully');
      })
      .catch((err) => {
        this.loadingResponse = false;
        Message.error(err.response.data.msg || 'Document could not be created');
      });
  }

  public trashDocument(id: string) {
    return new Promise((resolve) => {
      trashDocument(id)
        .then((res: any) => {
          this.getDocuments();
          Message.success('Document sent to trash');
          resolve(true);
        })
        .catch((err) => {
          this.loadingResponse = false;
          Message.error('Document could not be trashed. Please try again later');
          resolve(true);
        });
    });
  }

  public getDocuments() {
    this.payloadStore.change({ loadingResponse: true });
    fetchDocuments()
      .then((res: any) => {
        this.payloadStore.change({ documentsCount: res.length });
        if (res.length === 0 && localStorage.getItem('tour-status') !== 'inactive') {
          this.formData.title = "My First Text";
          this.newDocument();
        }
        this.documents = res.filter((e: any) => !e.is_deleted);
        this.payloadStore.change({ loadingResponse: false });
        this.checkNewDocumentDialog();
      })
      .catch((err) => {
        this.payloadStore.change({ loadingResponse: false });
      });
  }

  public downloadDocument(text: string, title: string) {
    this.payloadStore.change({ loadingResponse: true });
    downloadDocument(this.payloadStore.payload.user.pk, text)
      .then((res: any) => {
        saveAs(res.data, `${title}.docx`);
        Message.success('Document downloaded successfully');
        this.payloadStore.change({ loadingResponse: false });
      })
      .catch((err) => {
        console.log(err);
        Message.error('Could not download document');
        this.payloadStore.change({ loadingResponse: false });
      });
  }

  public documents: any = [];
}
</script>

<style>
/* apply styles to the pop confirm container */
.ant-popover-inner {
  border-radius: 15px;
}
</style>
